import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { featuredVideos, latestVideos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import VideoCard from '@/components/base/VideoCard';
import { faqItems } from '@/mocks/faq';

function StatCounter({ target, label, suffix = '' }: { target: number; label: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const counted = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !counted.current) {
          counted.current = true;
          let start = 0;
          const duration = 1500;
          const step = target / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else { setCount(Math.floor(start)); }
          }, 16);
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-foreground-600 mt-1">{label}</div>
    </div>
  );
}

export default function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '超碰在线',
    url: 'https://www.chaopengzx.com',
    description: '超碰在线是领先的免费视频平台，提供海量高清视频资源，极速加载、流畅播放，无需注册即可畅享。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.chaopengzx.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '超碰在线',
    url: 'https://www.chaopengzx.com',
    description: '超碰在线是领先的免费视频平台，提供海量高清视频资源。',
    sameAs: ['https://weibo.com/chaopengzx', 'https://space.bilibili.com/chaopengzx'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20cinematic%20abstract%20composition%20with%20flowing%20warm%20golden%20and%20coral%20gradient%20waves%20against%20a%20deep%20dark%20background%2C%20modern%20entertainment%20atmosphere%2C%20lens%20flare%20effect%2C%20elegant%20and%20artistic%2C%20no%20text%2C%20no%20people%2C%20clean%20abstract%20design&width=1920&height=900&seq=hero-home-2026&orientation=landscape"
            alt="超碰在线 - 免费视频平台"
            title="超碰在线首页"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-heading tracking-tight">
              海量视频，<span className="text-primary-400">免费畅享</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              超碰在线每日更新海量高清视频，极速加载、流畅播放，无需注册
            </p>

            <div className="max-w-xl mx-auto relative mb-8">
              <input
                type="text"
                placeholder="搜索你喜欢的电影、电视剧、综艺..."
                className="w-full pl-12 pr-28 py-4 text-base rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/60 text-lg"></i>
              <button className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                搜索
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.slice(0, 6).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-white/85 text-sm hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-100 border-y border-background-200">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <StatCounter target={5000000} label="日活跃用户" suffix="+" />
              <StatCounter target={120000} label="视频总数" suffix="+" />
              <StatCounter target={99} label="播放成功率" suffix="%" />
              <StatCounter target={8000} label="每日更新" suffix="+" />
            </div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              分类浏览
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">海量内容，总有一款适合你</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group p-5 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300 mb-3">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-1">{cat.name}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{cat.description}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Videos */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-600">编辑精选，必看佳作</p>
              </div>
              <Link
                to="/category/dianying"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
              {featuredVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-2">
                最新更新
              </h2>
              <p className="text-sm text-foreground-600">每日新鲜内容，不间断更新</p>
            </div>
            <Link
              to="/category/zongyi"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
            {latestVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative w-full py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20gradient%20abstract%20background%20with%20soft%20overlapping%20circular%20shapes%20in%20coral%20and%20golden%20tones%2C%20minimal%20modern%20design%2C%20artistic%20composition%2C%20clean%20and%20elegant&width=1920&height=600&seq=cta-banner-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/30"></div>
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">
              随时随地，畅享高清视频
            </h2>
            <p className="text-white/75 text-sm md:text-base mb-8 max-w-lg mx-auto">
              支持PC、平板、手机多设备访问，无论在哪都能享受超碰在线带来的极致观影体验
            </p>
            <Link
              to="/category/dianying"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观看
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
              常见问题
            </h2>
            <p className="text-foreground-600 text-sm md:text-base">关于超碰在线，你想知道的都在这里</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, index) => (
              <details key={index} className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-semibold text-foreground-900 pr-4">{item.question}</span>
                  <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-500 group-open:text-background-50 transition-all">
                    <i className="ri-arrow-down-s-line text-sm group-open:rotate-180 transition-transform"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}